import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { ScrollTableContainer } from "../../components/visitors/Visitors";
import useApiEp from "../../hooks/useApiEp";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, toastError } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";
import CustomCircularP from "../../widgets/CustomCircularP";
import SearchBar from "../../widgets/searchBar";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import ExportExhibitorsButton from "./ExportExhibitorsButton";
import { getHeader } from "../../utils/utils2";

const ITEMS_PER_PAGE = 30;

const Exhibitors = () => {
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [sq, setSq] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-exhibitors?page=${page}&sq=${encodeURIComponent(sq)}&r=${reload}`
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSq(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const getFieldValue = (field) => field?.value ?? "-";

  const getContactName = (user) => {
    const salutation = user.contactSalutation?.value || "";
    const firstName = user.contactFirstName?.value || "";
    const lastName = user.contactLastName?.value || "";
    return `${salutation} ${firstName} ${lastName}`.trim() || "-";
  };

  const getInitials = (user) => {
    const company = user.companyName?.value || "";
    const contact = getContactName(user);
    const source = company || (contact === "-" ? "Exhibitor" : contact);
    return source
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  const getLocation = (user) => {
    const country = user.country?.value?.name || "";
    const state = user.state?.value?.name || "";
    const city = user.city?.value?.name || "";
    return [city, state, country].filter(Boolean).join(", ") || "-";
  };

  const getFloorAllocation = (user) => {
    const hallNumber = user.hallNumber?.value || "";
    const standNumber = user.standNumber?.value || "";

    if (hallNumber && standNumber) return `${hallNumber} / ${standNumber}`;
    return hallNumber || standNumber || "-";
  };

  const getIndustrySectors = (user) => {
    const sectors = user.industrySector?.value;
    return Array.isArray(sectors) && sectors.length > 0
      ? sectors.join(", ")
      : "-";
  };

  const totalItems = data?.totalItems || 0;
  const totalPages = data?.totalPages || Math.ceil(totalItems / ITEMS_PER_PAGE);
  const hasExhibitors = data?.data?.length > 0;

  const deleteAllExhibitors = async () => {
    setDeleting(true);
    setShowDelete(false);

    try {
      const response = await axios.delete(
        `${API_ENDPOINT}/admin/delete-all-exhibitors`,
        getHeader()
      );
      toast.success(response.data.message);
      setPage(1);
      setReload((current) => !current);
    } catch (deleteError) {
      toastError(deleteError);
    } finally {
      setDeleting(false);
    }
  };

  const headerCellSx = {
    bgcolor: "#f8fafc",
    color: "text.secondary",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.04em",
    py: 1.5,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, maxWidth: 1800, mx: "auto" }}>
      <BackBtn bstyle={{ mb: 1.5, px: 0 }} />
      <CustomCircularP show={isLoading} />

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          mb: 2.5,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            background:
              "linear-gradient(135deg, rgba(2, 71, 95, 0.08) 0%, rgba(255, 255, 255, 0) 62%)",
          }}
        >
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                bgcolor: "primary.main",
                color: "primary.contrastText",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <ApartmentOutlinedIcon />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  component="h1"
                  sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 750 }}
                >
                  Exhibitors
                </Typography>
                <Chip
                  label={totalItems.toLocaleString()}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Manage company profiles, contacts, and floor allocations.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            <Button
              onClick={() => navigate("/update-exhibitor-excel")}
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              sx={{ whiteSpace: "nowrap" }}
            >
              Bulk update
            </Button>
            <Button
              onClick={() => navigate("/add-exhibitor")}
              variant="contained"
              startIcon={<AddBusinessOutlinedIcon />}
              disableElevation
              sx={{ whiteSpace: "nowrap" }}
            >
              Add exhibitor
            </Button>
            <ExportExhibitorsButton />
          </Stack>
        </Box>

        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 560 }}>
            <SearchBar
              value={searchInput}
              placeholder="Search by company name…"
              size="small"
              onChange={(event) => setSearchInput(event.target.value)}
              inputProps={{ "aria-label": "Search exhibitors by company name" }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {sq
              ? `${totalItems.toLocaleString()} result${
                  totalItems === 1 ? "" : "s"
                } found`
              : `${totalItems.toLocaleString()} exhibitor${
                  totalItems === 1 ? "" : "s"
                } in the directory`}
          </Typography>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {getErrorResponse(error)}
        </Alert>
      )}

      {!isError && (
        <ScrollTableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Table aria-label="Exhibitors directory" sx={{ minWidth: 1320 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Company</TableCell>
                <TableCell sx={headerCellSx}>Contact person</TableCell>
                <TableCell sx={headerCellSx}>Email</TableCell>
                <TableCell sx={headerCellSx}>Phone</TableCell>
                <TableCell sx={headerCellSx}>Fascia</TableCell>
                <TableCell sx={headerCellSx}>Location</TableCell>
                <TableCell sx={headerCellSx}>Hall / Stand</TableCell>
                <TableCell sx={headerCellSx}>Industry sector</TableCell>
                <TableCell sx={headerCellSx}>Pin</TableCell>
                <TableCell align="right" sx={headerCellSx}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && !hasExhibitors ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 9 }}>
                    <SearchOffOutlinedIcon
                      sx={{ fontSize: 44, color: "text.disabled", mb: 1 }}
                    />
                    <Typography variant="subtitle1" fontWeight={700}>
                      {sq
                        ? "No exhibitors match your search"
                        : "No exhibitors yet"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {sq
                        ? "Try a different company name or clear the search."
                        : "Add your first exhibitor to start building the directory."}
                    </Typography>
                    {sq && (
                      <Button
                        size="small"
                        sx={{ mt: 1.5 }}
                        onClick={() => setSearchInput("")}
                      >
                        Clear search
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((user, index) => (
                  <TableRow
                    key={user._id || index}
                    hover
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "& td": { py: 1.5 },
                    }}
                  >
                    <TableCell sx={{ minWidth: 250 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          src={user.profile_image || ""}
                          alt={getFieldValue(user.companyName)}
                          variant="rounded"
                          sx={{
                            width: 42,
                            height: 42,
                            bgcolor: "rgba(2, 71, 95, 0.10)",
                            color: "primary.main",
                            fontSize: 14,
                            fontWeight: 700,
                          }}
                        >
                          {getInitials(user)}
                        </Avatar>
                        {user._id ? (
                          <Typography
                            component={Link}
                            to={`/exhibitor/${user._id}`}
                            variant="body2"
                            sx={{
                              color: "primary.main",
                              fontWeight: 700,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {getFieldValue(user.companyName)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" fontWeight={700}>
                            {getFieldValue(user.companyName)}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {getContactName(user)}
                    </TableCell>
                    <TableCell>
                      {user.contactEmail?.value ? (
                        <Box
                          component="a"
                          href={`mailto:${user.contactEmail.value}`}
                          sx={{
                            color: "text.primary",
                            textDecoration: "none",
                            "&:hover": { color: "primary.main" },
                          }}
                        >
                          {user.contactEmail.value}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {getFieldValue(user.phone)}
                    </TableCell>
                    <TableCell>{getFieldValue(user.fascia)}</TableCell>
                    <TableCell sx={{ minWidth: 210 }}>
                      {getLocation(user)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {getFloorAllocation(user)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 210, maxWidth: 300 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={getIndustrySectors(user)}
                      >
                        {getIndustrySectors(user)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getFieldValue(user.pin)}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {user._id && (
                        <Tooltip title="Edit exhibitor">
                          <IconButton
                            component={Link}
                            to={`/edit-exhibitor-form/${user._id}`}
                            size="small"
                            color="primary"
                            aria-label={`Edit ${getFieldValue(
                              user.companyName
                            )}`}
                            sx={{ bgcolor: "rgba(2, 71, 95, 0.07)" }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollTableContainer>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent={hasExhibitors ? "space-between" : "center"}
        gap={2}
        sx={{ mt: 2.5, mb: 2 }}
      >
        {/* {hasExhibitors && (
          <LoadingButton
            startIcon={<DeleteOutlineIcon />}
            size="small"
            variant="text"
            color="error"
            onClick={() => setShowDelete(true)}
            loading={deleting}
            sx={{ alignSelf: { sm: "flex-start" } }}
          >
            Delete all exhibitors
          </LoadingButton>
        )} */}

        {totalPages > 1 && (
          <Pagination
            page={page}
            onChange={(_, nextPage) => setPage(nextPage)}
            count={totalPages}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            sx={{ alignSelf: "center" }}
          />
        )}
      </Stack>

      <SimpleYesNoDialog
        title="Delete all exhibitors?"
        message="This will permanently remove every exhibitor profile. This action cannot be undone."
        onYes={deleteAllExhibitors}
        show={showDelete}
        setShow={setShowDelete}
      />
    </Box>
  );
};

export default Exhibitors;
